import React, { createContext, useContext, useState, useEffect } from "react";
import { db } from "lib/firebase";
import { doc, collection, onSnapshot } from "firebase/firestore";
import * as localConstants from "../constants";
import { roundTo } from "../components/utils/mathUtils";

const ConfigContext = createContext();

export const useConfig = () => {
    return useContext(ConfigContext);
};

export const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useState(localConstants);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // We will listen to multiple documents/collections
        // 1. settings/general
        // 2. settings/technical
        // 3. settings/pricing
        // 4. regions collection

        let newConfig = { ...localConstants };
        const unsubscribes = [];

        // Helper to merge updates
        const updateConfig = (collectionName, data) => {
            let mappedData = {};

            if (collectionName === "general") {
                // Map Spanish fields to App Constants
                if (data.Empresa) mappedData.COMPANY_NAME = data.Empresa;
                if (data.No_Llamadas) mappedData.PHONE_NUMBER = String(data.No_Llamadas);
                if (data.No_Whatsapp) mappedData.WHATSAPP_NUMBER = String(data.No_Whatsapp);
                if (data.Correo) mappedData.EMAIL_ADDRESS = String(data.Correo);
                if (data.Plantas) mappedData.PLANTAS_SOLARES = Number(data.Plantas);
                if (data.ReduccionFactura) mappedData.REDUCE_FACT = Number(data.ReduccionFactura);
                if (data.PorMinimoPago) mappedData.MIN_PORCENT_FACT = Number(data.PorMinimoPago);
                if (data.AceptacionClientes) mappedData.SATISFACTION_CLIENT = Number(data.AceptacionClientes);
                if (data.InicioLabores) mappedData.INICIO_DESDE = Number(data.InicioLabores);
                if (data.PorAhorroPromedio) mappedData.AHORRO_PROMEDIO = Number(data.PorAhorroPromedio);
                if (data.PorDescPagoContado) mappedData.DESCUENTO_CONTADO = Number(data.PorDescPagoContado);
                if (data.TasaInteresSV) mappedData.TASA_INTERES = Number(data.TasaInteresSV);
            } else if (collectionName === "technical") {
                if (data.Costo_Energia_SinPanel) mappedData.COSTO_ENERGIA_PROM_SINPANEL = Number(data.Costo_Energia_SinPanel);
                if (data.Costo_Energía_ConPanel) mappedData.COSTO_ENERGIA_PROM_CONPANEL = Number(data.Costo_Energía_ConPanel);
                if (data.kWh_Mes_Panel) mappedData.KWH_MES_PANEL = Number(data.kWh_Mes_Panel);
                if (data.Garantia) mappedData.GARANTY_YEARS = Number(data.Garantia);
                if (data.PanelesMaxSistema) mappedData.MAX_PANELES_POR_SISTEMA = Number(data.PanelesMaxSistema);
                if (data.kWh_Dia_panel) mappedData.KWH_DIA_PANEL = Number(data.kWh_Dia_panel);
                if (data.PorFijoFactura) mappedData.PORCENTAJE_PAGO_FIJO = Number(data.PorFijoFactura);

                const FactPromAnualCO2 = Number(data.FactPromAnualCO2);
                const FactPromAnualCarbon = Number(data.FactPromAnualCarbon);
                const FactPromAnualArboles = Number(data.FactPromAnualArboles);
                const FactPromAnualCarrosOut = Number(data.FactPromAnualCarrosOut);
                const TotalEnergia = Number(data.TotalEnergia);

                if (FactPromAnualCO2) mappedData.FACT_CO2_PROMEDIO_ANUAL = FactPromAnualCO2;
                if (FactPromAnualCarbon) mappedData.FACT_CARBON_PROM_ANUAL = FactPromAnualCarbon;
                if (FactPromAnualArboles) mappedData.FACT_ARBOLES_PROM_ANUAL = FactPromAnualArboles;
                if (FactPromAnualCarrosOut) mappedData.FACT_CARS_OFF_PROM_ANUAL = FactPromAnualCarrosOut;

                if (TotalEnergia) mappedData.ENERGIA_TOTAL = TotalEnergia;
                if (data.TotalAhorro) mappedData.AHORRO_TOTAL = Number(data.TotalAhorro);
                const CO2TotalTemp = roundTo(TotalEnergia * FactPromAnualCO2, 2)
                mappedData.CO2_TOTAL = CO2TotalTemp;
                mappedData.CARBON_TOTAL = roundTo(CO2TotalTemp * FactPromAnualCarbon, 2);
                mappedData.TOTAL_ARBOLES = roundTo(CO2TotalTemp * FactPromAnualArboles, 0);
                mappedData.CARS_OFF_TOTAL = roundTo(CO2TotalTemp / FactPromAnualCarrosOut, 0);

                // Map GeneracionAproxMensual (Map) to monthlyVariation (Array)
                if (data.GeneracionAproxMensual) {
                    const gen = data.GeneracionAproxMensual;
                    // Order: Ene, Feb, Mar, Abr, May, Jun, Jul, Ago, Sep, Oct, Nov, Dic
                    // User keys might be "Ene", "Feb", etc.
                    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
                    const variationArray = months.map(m => gen[m] ? Number(gen[m]) : 1.0);
                    mappedData.monthlyVariation = variationArray;
                }
            }

            setConfig(prevConfig => {
                const updated = { ...prevConfig, ...mappedData };

                // Re-calculate derived values
                const phone = updated.PHONE_NUMBER;
                const whatsapp = updated.WHATSAPP_NUMBER;

                const formatGwPhone = (num) => {
                    if (!num) return "";
                    const s = String(num);
                    return `+502 ${s.substring(0, 4)}-${s.substring(4, 8)}`;
                };

                if (phone) {
                    updated.CALL_PHONE_NUMBER = `+502${phone}`;
                    updated.PHONE_NUMBER_FORM = formatGwPhone(phone);
                }
                if (whatsapp) {
                    updated.WHATSAPP_NUMBER_CHAT = `+502${whatsapp}`;
                    updated.WHATSAPP_NUMBER_FORM = formatGwPhone(whatsapp);
                }

                return updated;
            });
        };

        // settings/general
        unsubscribes.push(onSnapshot(doc(db, "settings", "general"), (doc) => {
            if (doc.exists()) {
                //console.log("✅ Firestore: Config General cargada", doc.data());
                updateConfig("general", doc.data());
            } else {
                console.warn("⚠️ Firestore: Documento 'settings/general' NO EXISTE. Verifica el ID del documento en Firebase.");
            }
        }, (error) => console.error("❌ Error cargando settings/general:", error)));

        // settings/technical
        unsubscribes.push(onSnapshot(doc(db, "settings", "technical"), (doc) => {
            if (doc.exists()) {
                //console.log("✅ Firestore: Config Technical cargada", doc.data());
                updateConfig("technical", doc.data());
            } else {
                console.warn("⚠️ Firestore: Documento 'settings/technical' NO EXISTE. Verifica el ID del documento en Firebase.");
            }
        }, (error) => console.error("❌ Error cargando settings/technical:", error)));

        // settings/pricing -> NOW using 'precios' collection
        unsubscribes.push(onSnapshot(collection(db, "precios"), (snapshot) => {
            const pricingData = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                pricingData.push({
                    paneles: Number(data.paneles),
                    inversion: Number(data.inversion)
                });
            });

            // Sort descending by number of panels (compatibility with infoPanel.js)
            pricingData.sort((a, b) => b.paneles - a.paneles);

            if (pricingData.length > 0) {
                setConfig(prev => ({ ...prev, paneles_solares: pricingData }));
            }
        }, (error) => console.error("❌ Error cargando precios:", error)));

        // regions collection -> "regiones"
        unsubscribes.push(onSnapshot(collection(db, "regiones"), (snapshot) => {
            const regions = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                regions.push({
                    id: doc.id,
                    name: data.Nombre,
                    label: data.Label,
                    solarPotential: data.Potencia_Solar,
                    porcentaje: Number(data.Porcentaje),
                    coordinates: {
                        x: data.Coordenadas?.x ? `${data.Coordenadas.x}%` : "50%",
                        y: data.Coordenadas?.y ? `${data.Coordenadas.y}%` : "50%"
                    },
                    active: data.Activa,
                    ...data
                });
            });

            //console.log(`✅ Firestore: ${regions.length} regiones cargadas`, regions);

            if (regions.length > 0) {
                setConfig(prev => ({ ...prev, guatemalaRegions: regions }));
            } else {
                console.warn("⚠️ Firestore: La colección 'regiones' está vacía o no se encontró.");
            }
        }, (error) => console.error("❌ Error cargando regiones:", error)));

        // finish loading
        setLoading(false);

        return () => {
            unsubscribes.forEach(unsub => unsub());
        };
    }, []);

    const value = {
        ...config,
        loading
    };

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    );
};
